// Per-employee sidebar preferences (pinned tools, recently visited, rail width).
// localStorage only — this is personal layout state, not authorization, and the
// ids are re-checked against the server-filtered uiConfig before anything is
// rendered, so a stale pin for a tool you lost access to simply doesn't show up.
// Mirrors the storage pattern in utils/googleSheetRecents.js.

const PINNED_PREFIX = "navPinned";
const RECENT_PREFIX = "navRecent";
const COLLAPSED_KEY = "navSidebarCollapsed";

export const MAX_PINNED_NAV_ITEMS = 8;
export const MAX_RECENT_NAV_ITEMS = 5;

const storageKey = (prefix, employeeId) => `${prefix}:${employeeId || "anonymous"}`;

const readIds = (prefix, employeeId) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(prefix, employeeId)) || "[]");
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
};

const writeIds = (prefix, employeeId, ids) => {
  try {
    localStorage.setItem(storageKey(prefix, employeeId), JSON.stringify(ids));
  } catch {
    // Ignore storage failures — pinning is a convenience, never a blocker.
  }
  return ids;
};

export const getPinnedNavIds = (employeeId) => readIds(PINNED_PREFIX, employeeId);

/** Adds or removes an id and returns the new list (newest pin last). */
export const togglePinnedNavId = (employeeId, itemId) => {
  if (!itemId) return getPinnedNavIds(employeeId);
  const current = getPinnedNavIds(employeeId);
  const next = current.includes(itemId)
    ? current.filter((id) => id !== itemId)
    : [...current, itemId].slice(-MAX_PINNED_NAV_ITEMS);
  return writeIds(PINNED_PREFIX, employeeId, next);
};

export const getRecentNavIds = (employeeId) => readIds(RECENT_PREFIX, employeeId);

/** Moves an id to the front of the recents list and returns the new list. */
export const markRecentNavId = (employeeId, itemId) => {
  if (!itemId) return getRecentNavIds(employeeId);
  const current = getRecentNavIds(employeeId);
  if (current[0] === itemId) return current;
  const next = [itemId, ...current.filter((id) => id !== itemId)].slice(
    0,
    MAX_RECENT_NAV_ITEMS
  );
  return writeIds(RECENT_PREFIX, employeeId, next);
};

export const getSidebarCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
};

export const setSidebarCollapsed = (collapsed) => {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "true" : "false");
  } catch {
    // Ignore storage failures.
  }
  return collapsed;
};
